import { createAsyncThunk, createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { serializeReduxError } from "./thunkExtra.js";

export type CmtsRoleEntity = { readonly id: string; readonly code: string; readonly name: string };
export type CmtsPermissionEntity = { readonly id: string; readonly code: string; readonly name: string; readonly description?: string | undefined };
export type CmtsBackendMenuItem = { readonly id: string; readonly label: string; readonly href?: string | undefined; readonly permission?: string | undefined; readonly parentId?: string | undefined; readonly order?: number | undefined };
export type CmtsPermissionBlock = { readonly permission?: string | undefined; readonly route?: string | undefined; readonly message: string; readonly status?: number | undefined; readonly correlationId?: string | undefined; readonly atUtc: string };

const roleAdapter = createEntityAdapter<CmtsRoleEntity>();
const permissionAdapter = createEntityAdapter<CmtsPermissionEntity>();
const menuAdapter = createEntityAdapter<CmtsBackendMenuItem>();

export const fetchCmtsPermissions = createAsyncThunk<readonly string[], void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "permission/fetchPermissions",
  async (_arg, thunkApi) => {
    try {
      if (!thunkApi.extra.authRepository) {
        return thunkApi.rejectWithValue({ message: "Auth repository is not available for permission refresh.", atUtc: new Date().toISOString() });
      }
      return await thunkApi.extra.authRepository.myPermissions();
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Permission refresh failed."));
    }
  },
);

export const fetchCmtsBackendMenu = createAsyncThunk<readonly CmtsBackendMenuItem[], void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "permission/fetchMenu",
  async (_arg, thunkApi) => {
    try {
      if (!thunkApi.extra.authRepository) {
        return thunkApi.rejectWithValue({ message: "Auth repository is not available for menu refresh.", atUtc: new Date().toISOString() });
      }
      return await thunkApi.extra.authRepository.myMenu();
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Menu refresh failed."));
    }
  },
);

export type CmtsPermissionState = {
  readonly roles: ReturnType<typeof roleAdapter.getInitialState>;
  readonly permissions: ReturnType<typeof permissionAdapter.getInitialState>;
  readonly menu: ReturnType<typeof menuAdapter.getInitialState>;
  readonly loading: boolean;
  readonly error?: CmtsSerializableError | undefined;
  readonly lastLoadedAtUtc?: string | undefined;
  readonly lastPermissionBlock?: CmtsPermissionBlock | undefined;
};

const initialState: CmtsPermissionState = {
  roles: roleAdapter.getInitialState(),
  permissions: permissionAdapter.getInitialState(),
  menu: menuAdapter.getInitialState(),
  loading: false,
};

function toRoleEntity(code: string): CmtsRoleEntity {
  return { id: code, code, name: code };
}

function toPermissionEntity(code: string): CmtsPermissionEntity {
  return { id: code, code, name: code };
}

const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    permissionsLoaded(state, action: PayloadAction<{ permissions: readonly string[]; roles?: readonly string[] | undefined }>) {
      permissionAdapter.setAll(state.permissions, action.payload.permissions.map(toPermissionEntity));
      if (action.payload.roles) roleAdapter.setAll(state.roles, action.payload.roles.map(toRoleEntity));
      state.loading = false;
      state.error = undefined;
      state.lastLoadedAtUtc = new Date().toISOString();
    },
    menuLoaded(state, action: PayloadAction<readonly CmtsBackendMenuItem[]>) {
      menuAdapter.setAll(state.menu, action.payload.map((item) => ({ ...item })));
      state.loading = false;
      state.error = undefined;
      state.lastLoadedAtUtc = new Date().toISOString();
    },
    permissionBlocked(state, action: PayloadAction<Omit<CmtsPermissionBlock, "atUtc">>) {
      state.lastPermissionBlock = { ...action.payload, atUtc: new Date().toISOString() };
    },
    permissionsCleared(state) {
      roleAdapter.removeAll(state.roles);
      permissionAdapter.removeAll(state.permissions);
      menuAdapter.removeAll(state.menu);
      state.loading = false;
      state.error = undefined;
      state.lastLoadedAtUtc = undefined;
      state.lastPermissionBlock = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsPermissions.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsPermissions.fulfilled, (state, action) => {
        permissionAdapter.setAll(state.permissions, action.payload.map(toPermissionEntity));
        state.loading = false;
        state.lastLoadedAtUtc = new Date().toISOString();
      })
      .addCase(fetchCmtsPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Permission refresh failed.");
      })
      .addCase(fetchCmtsBackendMenu.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsBackendMenu.fulfilled, (state, action) => {
        menuAdapter.setAll(state.menu, action.payload.map((item) => ({ ...item })));
        state.loading = false;
        state.lastLoadedAtUtc = new Date().toISOString();
      })
      .addCase(fetchCmtsBackendMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Menu refresh failed.");
      });
  },
});

export const roleEntityAdapter = roleAdapter;
export const permissionEntityAdapter = permissionAdapter;
export const menuEntityAdapter = menuAdapter;
export const permissionActions = permissionSlice.actions;
export const permissionReducer = permissionSlice.reducer;
